import React, {useState, useEffect, useMemo, useRef} from 'react';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTextArea from '../../../common/arr-text-area/ArrTextArea';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {setAssignmentInstructions} from '../../../../redux/slice/assignmentSlice';
import './InstructionsFlyout.scss';

// Constants
// ----------------------------------
// HTML maxLength maximum is 2,147,483,647
const MAX_TEXTAREA_LENGTH = 2147483647; // Maximum value for HTML maxLength attribute

// Types Definitions
// ----------------------------------
interface InstructionsFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
}
// ------------------

// ----------------------------------
// Instructions Component
// ----------------------------------
const InstructionsFlyout: React.FC<InstructionsFlyoutProps> = ({
  isOpen = false,
  onClose,
}) => {
  const dispatch = useDispatch();
  const reduxInstructions = useSelector(
    (state: RootState) =>
      state.assignment.formData.assignmentModel.assignmentInstructions || ''
  );
  const {parentAssignmentGroupID} = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );
  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;
  const [localInstructions, setLocalInstructions] =
    useState<string>(reduxInstructions);
  const prevIsOpenRef = useRef<boolean>(false);

  useEffect(() => {
    const drawerJustOpened = isOpen && !prevIsOpenRef.current;

    if (drawerJustOpened) {
      const reduxValue = reduxInstructions || '';
      setLocalInstructions(reduxValue);
    }

    // Update the ref after checking the condition
    prevIsOpenRef.current = isOpen;
  }, [isOpen, reduxInstructions]);

  const isSubmitDisable = useMemo(() => {
    if (!isOpen) {
      return true; // Disable when drawer is closed
    }
    const localValue = localInstructions || '';
    const reduxValue = reduxInstructions || '';
    const hasChanges = localValue !== reduxValue;
    return !hasChanges; // Disable when there are no changes
  }, [localInstructions, reduxInstructions, isOpen]);

  function handleSave(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    event.preventDefault();
    if (!isSubmitDisable) {
      const valueToSave = localInstructions || '';
      // Dispatch the action - update the redux state
      dispatch(setAssignmentInstructions(valueToSave));
      onClose();
    }
  }

  const handleInstructionsChange = (value: string) => {
    setLocalInstructions(value);
  };

  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title=""
      side="right"
      size="small"
      showButton={false}
      showHeader={false}
      className="Instructions_flyout"
      ariaLabel="InstructionsFlyout"
    >
      <Box className="Instructions_flyout__header">
        <Box className="title-box">
          <Typography
            className=" typo-text headline3_size title"
            data-testid="InstructionsFlyout-title"
          >
            {t(`assignment.new.instructions`)}&nbsp;
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <InfoIcon
                fontSize="medium"
                className="info-icon"
                data-testid="instructions-title-info"
              />
            }
            title={t('assignment.new.instructionsFlyout.title')}
          >
            {t('assignment.new.instructionsFlyout.description')}
          </ArrTooltipWrapper>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={onClose}
            disabled={false}
            aria-label={t('common.cancel')}
            dataTestId="instructions-flyout-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            disabled={isSubmitDisable}
            variant="primary"
            size="medium"
            type="submit"
            aria-label={t('common.save')}
            dataTestId="save-button"
            onClick={handleSave}
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="Instructions_flyout__inputLabel">
        <Box className="Instructions_flyout__content">
          <ArrTextArea
            dataTestId="instructions-text-area"
            placeholder={t('assignment.new.instructionsFlyout.label')}
            value={localInstructions}
            onChange={handleInstructionsChange}
            aria-label={t('instructions.label')}
            maxLength={MAX_TEXTAREA_LENGTH}
            disabled={isParentCascaseEnabled}
          />
        </Box>
      </Box>
    </ArrDrawer>
  );
};
export {InstructionsFlyout};
export default InstructionsFlyout;
